import React, { useRef } from "react";

import styled from "styled-components";

import { FigureModalSource, inferFigureTextFromPath } from "./figure-modal-context";
import { handleImagePaste, isAllowedImageType } from "./paste-image";

const Overlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 40;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(15, 23, 42, 0.28);
    backdrop-filter: blur(2px);
`;

const Card = styled.div`
    display: flex;
    flex-direction: column;
    width: min(760px, 100%);
    max-height: min(720px, calc(100vh - 96px));
    border: 1px solid #d8e1ec;
    border-radius: 14px;
    background: #ffffff;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 18px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
`;

const Title = styled.div`
    color: #17314a;
    font-size: 16px;
    font-weight: 700;
`;

const Body = styled.div`
    display: grid;
    gap: 16px;
    padding: 18px;
    overflow: auto;
`;

const Field = styled.div`
    display: grid;
    gap: 8px;
`;

const Label = styled.div`
    color: #42576b;
    font-size: 12px;
    font-weight: 700;
`;

const HeaderActions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

const Input = styled.input`
    width: 100%;
    height: 36px;
    padding: 0 12px;
    border: 1px solid #d9e2ec;
    border-radius: 8px;
    background: #ffffff;
    color: #24384d;
    font-size: 13px;
    outline: none;

    &:focus {
        border-color: #0f8f6f;
        box-shadow: 0 0 0 3px rgba(15, 143, 111, 0.12);
    }
`;

const Value = styled.div`
    color: #5c7187;
    font-size: 12px;
    line-height: 1.5;
`;

const FileList = styled.div`
    display: grid;
    gap: 8px;
    max-height: 180px;
    overflow: auto;
`;

const FileItem = styled.button`
    display: grid;
    gap: 2px;
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d9e2ec;
    border-radius: 8px;
    background: #ffffff;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: #f8fbfd;
        border-color: #bfcddd;
    }
`;

const FileName = styled.span`
    color: #24384d;
    font-size: 12px;
    font-weight: 700;
`;

const FilePath = styled.span`
    color: #6c8196;
    font-size: 11px;
    word-break: break-all;
`;

const UploadDropzone = styled.div`
    display: grid;
    gap: 6px;
    padding: 18px;
    border: 1px dashed #b9c8d8;
    border-radius: 10px;
    background: #f8fbfd;
    text-align: center;
`;

const UploadTitle = styled.div`
    color: #24384d;
    font-size: 13px;
    font-weight: 700;
`;

const UploadSubtitle = styled.div`
    color: #698096;
    font-size: 12px;
`;

const UploadCurrent = styled.div`
    color: #0f8f6f;
    font-size: 12px;
    font-weight: 700;
    word-break: break-all;
`;

const UploadActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const CheckboxRow = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #24384d;
    font-size: 12px;
    font-weight: 700;
`;

const OptionText = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #24384d;
    font-size: 12px;
    font-weight: 700;
`;

const OptionHint = styled.span`
    color: #6c8196;
    font-size: 11px;
    font-weight: 500;
`;

const WidthGroup = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
`;

const WidthButton = styled.button`
    height: 34px;
    padding: 0 10px;
    border: 1px solid ${(props) => (props.$active ? "#0f8f6f" : "#d9e2ec")};
    border-radius: 8px;
    background: ${(props) => (props.$active ? "rgba(15, 143, 111, 0.08)" : "#ffffff")};
    color: ${(props) => (props.$active ? "#0f8f6f" : "#42576b")};
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
`;

const HelpPanel = styled.div`
    display: grid;
    gap: 8px;
    padding: 14px 16px;
    border: 1px solid #d9e2ec;
    border-radius: 10px;
    background: #f8fbfd;
    color: #536981;
    font-size: 12px;
    line-height: 1.5;
`;

const Section = styled.div`
    display: grid;
    gap: 12px;
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 16px 18px;
    border-top: 1px solid #e2e8f0;
    background: #f8fafc;
`;

const Button = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 34px;
    padding: 0 14px;
    border: 1px solid ${(props) => (props.$primary ? "#12805f" : "#d9e2ec")};
    border-radius: 8px;
    background: ${(props) => (props.$primary ? "#138a6d" : "#ffffff")};
    color: ${(props) => (props.$primary ? "#ffffff" : "#42576b")};
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

function ManuscriptFigureModal({
    activeFigureData,
    figureDraft,
    figureModalSource,
    isFigureUploadPending,
    isImageBinary,
    projectFigureFiles,
    selectedNode,
    onClose,
    onDelete,
    onApply,
    onSourceChange,
    onDraftChange,
    onUploadFile,
}) {
    const fileInputRef = useRef(null);
    const widthPresets = [
        { value: "0.25", label: "1/4" },
        { value: "0.5", label: "1/2" },
        { value: "0.75", label: "3/4" },
        { value: "1", label: "Full" },
    ];

    if (!figureDraft) {
        return null;
    }

    const isEditMode = figureModalSource === FigureModalSource.EDIT_FIGURE;
    const titleBySource = {
        [FigureModalSource.FILE_UPLOAD]: "Upload From Computer",
        [FigureModalSource.FILE_TREE]: "Insert From Project Files",
        [FigureModalSource.FROM_URL]: "Insert From URL",
        [FigureModalSource.EDIT_FIGURE]: "Edit Figure",
    };

    const title = titleBySource[figureModalSource] || "Insert Image";
    const selectedWidth = String(figureDraft?.width ?? "");

    const updateDraft = (patch) => {
        onDraftChange((current) => ({
            ...current,
            ...patch,
        }));
    };

    const handlePathChange = (event) => {
        const nextPath = event.target.value;
        const inferred =
            figureModalSource === FigureModalSource.FROM_URL || figureModalSource === FigureModalSource.FILE_TREE
                ? inferFigureTextFromPath(nextPath, figureDraft)
                : {};

        updateDraft({
            path: nextPath,
            ...inferred,
        });
    };

    const handleProjectFilePick = (path) => {
        updateDraft({
            path,
            ...inferFigureTextFromPath(path, figureDraft),
        });
    };

    const handleUploadDrop = async (event) => {
        event.preventDefault();
        if (isFigureUploadPending) {
            return;
        }

        const file = event.dataTransfer?.files?.[0];
        if (!file || !isAllowedImageType(file.type)) {
            return;
        }

        await onUploadFile?.(file);
    };

    const renderSourceBody = () => {
        if (figureModalSource === FigureModalSource.FILE_UPLOAD) {
            return (
                <Section>
                    <Field className="manuscript-figure-modal-field">
                        <Label className="manuscript-figure-modal-label">Upload file</Label>
                        <UploadDropzone
                            className="manuscript-figure-modal-upload-dropzone"
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={handleUploadDrop}
                        >
                            <UploadTitle className="manuscript-figure-modal-upload-title">
                                파일을 드롭하거나 선택해서 업로드
                            </UploadTitle>
                            <UploadSubtitle className="manuscript-figure-modal-upload-subtitle">
                                PNG, JPG, SVG, PDF, PS 형식을 지원합니다.
                            </UploadSubtitle>
                            {figureDraft?.path ? (
                                <UploadCurrent className="manuscript-figure-modal-upload-current">
                                    {figureDraft.path}
                                </UploadCurrent>
                            ) : null}
                        </UploadDropzone>
                        <Value className="manuscript-figure-modal-value">
                            붙여넣기, 드롭, 파일 선택 중 어느 경로로 업로드해도 완료되면 프로젝트 파일 경로로
                            전환됩니다.
                        </Value>
                        <UploadActions className="manuscript-figure-modal-upload-actions">
                            <Button
                                type="button"
                                className="manuscript-figure-modal-button"
                                onClick={async () => {
                                    await handleImagePaste();
                                }}
                                disabled={isFigureUploadPending}
                            >
                                클립보드 이미지 붙여넣기
                            </Button>
                            <Button
                                type="button"
                                className="manuscript-figure-modal-button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isFigureUploadPending}
                            >
                                {isFigureUploadPending ? "업로드 중..." : "파일 선택"}
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                accept="image/*,.pdf,.ps,.eps"
                                onChange={async (event) => {
                                    const file = event.target.files?.[0];
                                    if (file) {
                                        await onUploadFile?.(file);
                                    }
                                    event.target.value = "";
                                }}
                            />
                        </UploadActions>
                    </Field>
                </Section>
            );
        }

        if (figureModalSource === FigureModalSource.FROM_URL) {
            return (
                <Section>
                    <Field className="manuscript-figure-modal-field">
                        <Label className="manuscript-figure-modal-label">Image URL / Path</Label>
                        <Input
                            className="manuscript-figure-modal-input"
                            value={figureDraft?.path || ""}
                            onChange={handlePathChange}
                            placeholder="https://example.com/figure.png"
                        />
                        <Value className="manuscript-figure-modal-value">
                            URL 또는 프로젝트 내부에서 접근 가능한 이미지 경로를 입력합니다.
                        </Value>
                    </Field>
                </Section>
            );
        }

        if (figureModalSource === FigureModalSource.FILE_TREE || figureModalSource === FigureModalSource.EDIT_FIGURE) {
            return (
                <Section>
                    <Field className="manuscript-figure-modal-field">
                        <Label className="manuscript-figure-modal-label">Project file path</Label>
                        <Input
                            className="manuscript-figure-modal-input"
                            value={figureDraft?.path || ""}
                            list="manuscript-figure-project-files"
                            onChange={handlePathChange}
                        />
                        <datalist id="manuscript-figure-project-files">
                            {(projectFigureFiles || []).map((file) => (
                                <option key={file.id || file.path} value={file.path}>
                                    {file.name || file.path}
                                </option>
                            ))}
                        </datalist>
                        {figureModalSource === FigureModalSource.FILE_TREE && !isImageBinary ? (
                            <Value className="manuscript-figure-modal-value">
                                현재 선택된 이미지 파일이 없습니다. 파일 트리에서 이미지 노드를 선택한 상태에서 열면
                                경로가 자동으로 채워집니다.
                            </Value>
                        ) : null}
                    </Field>
                    {(projectFigureFiles || []).length > 0 ? (
                        <Field className="manuscript-figure-modal-field">
                            <Label className="manuscript-figure-modal-label">Project files</Label>
                            <FileList className="manuscript-figure-modal-file-list">
                                {(projectFigureFiles || []).slice(0, 8).map((file) => (
                                    <FileItem
                                        key={file.id || file.path}
                                        type="button"
                                        className="manuscript-figure-modal-file-item"
                                        onClick={() => handleProjectFilePick(file.path)}
                                    >
                                        <FileName className="manuscript-figure-modal-file-name">
                                            {file.name || file.path}
                                        </FileName>
                                        <FilePath className="manuscript-figure-modal-file-path">{file.path}</FilePath>
                                    </FileItem>
                                ))}
                            </FileList>
                        </Field>
                    ) : null}
                </Section>
            );
        }

        return null;
    };

    return (
        <Overlay
            className="manuscript-figure-modal-overlay"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <Card className="manuscript-figure-modal-card">
                <Header className="manuscript-figure-modal-header">
                    <Title className="manuscript-figure-modal-title">{title}</Title>
                    <HeaderActions>
                        <Button
                            type="button"
                            className="manuscript-figure-modal-button"
                            onClick={() => updateDraft({ helpShown: !figureDraft?.helpShown })}
                        >
                            {figureDraft?.helpShown ? "Back" : "Help"}
                        </Button>
                        <Button type="button" className="manuscript-figure-modal-button" onClick={onClose}>
                            닫기
                        </Button>
                    </HeaderActions>
                </Header>
                <Body className="manuscript-figure-modal-body">
                    {figureDraft?.helpShown ? (
                        <HelpPanel>
                            <div>Choose an image source, then insert the figure into the editor.</div>
                            <div>
                                Upload accepts pasted, dropped, or selected files and switches back to a project path
                                after upload.
                            </div>
                            <div>Width is relative to the text width of the document.</div>
                            <div>
                                Caption and label options follow the Overleaf toolbar behavior and insert standard LaTeX
                                placeholders.
                            </div>
                        </HelpPanel>
                    ) : null}

                    {!figureDraft?.helpShown ? (
                        <>
                            {renderSourceBody()}

                            <Field className="manuscript-figure-modal-field">
                                <CheckboxRow className="manuscript-figure-modal-checkbox-row">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(figureDraft?.includeCaption)}
                                        onChange={(event) => updateDraft({ includeCaption: event.target.checked })}
                                    />
                                    <OptionText>Include caption</OptionText>
                                </CheckboxRow>
                                <OptionHint>{"Insert a standard \\caption{...} line for the figure."}</OptionHint>
                            </Field>

                            <Field className="manuscript-figure-modal-field">
                                <CheckboxRow className="manuscript-figure-modal-checkbox-row">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(figureDraft?.includeLabel)}
                                        onChange={(event) => updateDraft({ includeLabel: event.target.checked })}
                                    />
                                    <OptionText>
                                        Include label
                                        <OptionHint>Used for cross references in the document.</OptionHint>
                                    </OptionText>
                                </CheckboxRow>
                            </Field>

                            <Field className="manuscript-figure-modal-field">
                                <Label className="manuscript-figure-modal-label">Image width</Label>
                                <WidthGroup>
                                    {widthPresets.map((preset) => (
                                        <WidthButton
                                            key={preset.value}
                                            type="button"
                                            $active={selectedWidth === preset.value}
                                            onClick={() => updateDraft({ width: preset.value })}
                                        >
                                            {preset.label}
                                        </WidthButton>
                                    ))}
                                </WidthGroup>
                            </Field>

                            {activeFigureData ? (
                                <Field className="manuscript-figure-modal-field">
                                    <Label className="manuscript-figure-modal-label">Range</Label>
                                    <Value className="manuscript-figure-modal-value">
                                        {activeFigureData.from}-{activeFigureData.to}
                                    </Value>
                                </Field>
                            ) : null}
                        </>
                    ) : null}
                </Body>

                <Footer className="manuscript-figure-modal-footer">
                    {activeFigureData ? (
                        <Button type="button" className="manuscript-figure-modal-button" onClick={onDelete}>
                            삭제
                        </Button>
                    ) : null}
                    <Button type="button" className="manuscript-figure-modal-button" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        className="manuscript-figure-modal-button manuscript-figure-modal-button-primary"
                        $primary
                        onClick={onApply}
                        disabled={
                            figureDraft?.helpShown ||
                            isFigureUploadPending ||
                            (!isEditMode && !String(figureDraft?.path || "").trim())
                        }
                    >
                        {isEditMode ? "Done" : "Insert Figure"}
                    </Button>
                </Footer>
            </Card>
        </Overlay>
    );
}

export default ManuscriptFigureModal;
